import { router } from "expo-router";
import Animated, { FadeIn } from "react-native-reanimated";

import { AnimatedScrollView } from "$src/core/components/atoms/ScrollView/ScrollView";
import Spinner from "$src/core/components/atoms/Spinner/Spinner";
import Text from "$src/core/components/atoms/Text/Text";
import ProfileSelector from "$src/core/components/organisms/ProfileSelector";
import useAuth from "$src/core/hooks/use-auth";
import useProfiles from "$src/core/hooks/use-profiles";
import useStackHeader from "$src/core/hooks/use-stack-header";
import useTranslation from "$src/core/hooks/use-translation";
import type { ProfileDto } from "$src/core/models/dto";
import ErrorPage from "$src/core/pages/ErrorPage/ErrorPage";

import $ from "./ProfilesPage.styles";

const ProfilesPage: React.FC = () => {
  const { t } = useTranslation();
  const { selectProfile } = useAuth();
  useStackHeader({
    left: "back",
  });

  const { profiles, status, error, refetch } = useProfiles();

  const handleSelectProfile = (profile: ProfileDto) => {
    selectProfile(profile.id);

    // Replacing remounts the tabs, and the native tab bar never re-measures.
    if (router.canGoBack()) {
      router.dismissTo("/storefront");
      return;
    }

    router.replace("/storefront");
  };

  const handleEditProfile = (profile: ProfileDto) => {
    router.navigate(`/profiles/${profile.id}`);
  };

  if (status === "error") {
    return (
      <ErrorPage
        title={t("ERROR/PROFILES_PAGE/TITLE")}
        subtitle={t("ERROR/PROFILES_PAGE/SUBTITLE")}
        ctaText={t("COMMON/RETRY")}
        ctaIcon="retry"
        onPressCta={refetch}
        error={error}
      />
    );
  }

  if (status === "pending" || !profiles) {
    return <Spinner />;
  }

  return (
    <AnimatedScrollView
      contentContainerStyle={$.container}
      scrollEventThrottle={16}
    >
      <Animated.View entering={FadeIn.duration(100)}>
        <Text preset="heading1" accessibilityRole="header">
          {t("PROFILE/TITLE")}
        </Text>
      </Animated.View>
      <Animated.View entering={FadeIn.duration(100).delay(100)}>
        <ProfileSelector
          profiles={profiles}
          onSelect={handleSelectProfile}
          onCreate={() => router.navigate("/profiles/create")}
          onEdit={handleEditProfile}
        />
      </Animated.View>
    </AnimatedScrollView>
  );
};

export default ProfilesPage;
